import { Alert, List, Skeleton, Tag, theme, Tooltip, Typography } from 'antd'
import { COMPONENT_TYPE_INFO } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsClassificationContent'
import { ClassifiedLta } from 'features/libra/stepComponents/ffs/classifiedLtas'
import { FFS_INDICATION_LAYER_PREFIX } from 'features/libra/stepComponents/ffs/ffsIndicationMarkup'
import { LtaClassificationForm } from 'features/libra/stepComponents/ffs/LtaClassificationForm'
import { useFfsLtaPrePlanning } from 'features/libra/stepComponents/ffs/useFfsLtaPrePlanning'
import { useFfsMarkupLayers } from 'features/libra/stepComponents/ffs/useFfsMarkupLayers'
import { useFfsTicketContext } from 'features/libra/stepComponents/ffs/useFfsTicketContext'
import { useLtaRulerDistances } from 'features/libra/stepComponents/ffs/useLtaRulerDistances'
import { useReportValidation } from 'features/libra/StepValidationContext'
import {
  LibraWorflowStepComponentProps,
  StepValidationResult,
} from 'features/libra/workflow/workflowStepsApi'
import { useFfsWorkspaceState } from 'features/libra/workspace/ffs/FfsWorkspaceStateContext'
import { useGeckoPermissions } from 'features/permissions'
import { FC, useMemo } from 'react'
import styled from 'styled-components'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

/**
 * FFS Pre-Planning step (runs AFTER LTA generation). Lists the generated LTAs;
 * the per-LTA classification form (`LtaClassificationForm`, keyed by the LTA id so
 * it remounts per selection) auto-derives corrosion type from each LTA's own
 * `customInfo.ffsStats`. The Asset Viz panel is a separate sibling driven by
 * workspace state (`useFfsWorkspaceState` + `useFfsMarkupLayers`). Classification
 * (persisted under the indication's `customInfo.ffsRegionClassification`) is
 * ADVISORY: it does not gate progress. The step can be advanced with LTAs still
 * unclassified — a warning is shown and Part 4/5 then run without a validated
 * route. Only genuine prerequisites (missing tags, no LTAs) block the step.
 */
export const Component: FC<
  LibraWorflowStepComponentProps<Record<string, never>>
> = ({ readonly = false }) => {
  const { component, componentId, inspectionId, componentModel, isLoading } =
    useFfsTicketContext()
  const { user } = useGeckoPermissions()
  const decidedBy = user?.name || user?.email || 'Unknown User'
  const { token } = theme.useToken()

  const {
    ltaSet,
    indications,
    classifiedLtas,
    saveClassification,
    isSaving,
    aggregate,
    isLoading: ltaLoading,
  } = useFfsLtaPrePlanning({ component, inspectionId })

  // Drive the shared Asset Viz panel exactly like the Part 4 / Part 5 steps:
  // selection + zoom live in workspace state, and `useFfsMarkupLayers` registers
  // the indications, LTA/flaw outlines, CTPs, and rulers the panel draws.
  const { selectedIndicationId, setSelectedIndicationId, setZoomToLayerId } =
    useFfsWorkspaceState()

  useFfsMarkupLayers({
    indications,
    ltaSetId: ltaSet?.id,
    component,
    inspectionId,
    selectedIndicationId,
  })

  // Distance to the nearest modelled feature per LTA. Advisory ONLY: it raises
  // a warning when something sits inside the Eq. 5.10 limit, and never feeds the
  // region default. Nothing waits on it — a warning that arrives a beat late is
  // fine, whereas gating the whole step on it would not be.
  const indicationIds = useMemo(
    () => indications.map((i) => i.id),
    [indications]
  )
  const remotenessByIndication = useLtaRulerDistances({
    indicationIds,
    componentModel,
  })

  const selected = useMemo(
    () =>
      classifiedLtas.find((l) => l.indication.id === selectedIndicationId) ??
      null,
    [classifiedLtas, selectedIndicationId]
  )

  // antd `List` wants a mutable `T[]`; `classifiedLtas` is a stable
  // ReadonlyArray, so memoize one copy instead of spreading every render.
  const dataSource = useMemo(() => [...classifiedLtas], [classifiedLtas])

  const validation = useMemo<StepValidationResult>(() => {
    if (isLoading || ltaLoading) {
      return { status: 'Warning', message: 'Loading LTAs...' }
    }
    if (!componentId || !inspectionId) {
      return {
        status: 'Error',
        message: 'Ticket is missing required component or inspection tag.',
      }
    }
    if (aggregate.totalCount === 0) {
      return {
        status: 'Warning',
        message:
          'No LTAs found. Generate Local Thin Areas in the LTA Generation step first.',
      }
    }
    // Classification is advisory, not gating: unclassified LTAs do NOT block
    // advancing (Pre-Planning is skippable). The unclassified count is surfaced
    // as a UI-only warning Alert in the render instead.
    return undefined
  }, [aggregate.totalCount, componentId, inspectionId, isLoading, ltaLoading])
  useReportValidation(validation)

  if (isLoading || ltaLoading) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    )
  }

  return (
    <Container>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        FFS Pre-Planning
      </Typography.Title>
      <Typography.Paragraph type='secondary' style={{ marginBottom: 0 }}>
        Classify each Local Thin Area&apos;s assessment region (API 579 Part 4).
        The classification determines the recommended Part 4 / Part 5 route for
        that LTA; a Type C region is not FFS-eligible. Classification is
        optional — you can proceed without it and assess the LTAs directly.
      </Typography.Paragraph>

      {aggregate.totalCount > 0 && !aggregate.allDecided ? (
        <Alert
          type='warning'
          showIcon
          message={`${aggregate.totalCount - aggregate.decidedCount} of ${aggregate.totalCount} LTA${aggregate.totalCount === 1 ? ' is' : 's are'} missing or invalid classification. You can proceed, but Part 4 / Part 5 will run without a validated route.`}
        />
      ) : null}

      <List
        size='small'
        bordered
        dataSource={dataSource}
        renderItem={({ indication, classification }: ClassifiedLta) => {
          return (
            <List.Item
              onClick={() => {
                setSelectedIndicationId(indication.id)
                // Frame the LTA in the viz (same one-shot zoom as Part 4/5).
                setZoomToLayerId(
                  `${FFS_INDICATION_LAYER_PREFIX}${indication.id}`
                )
              }}
              style={{
                cursor: 'pointer',
                background:
                  indication.id === selectedIndicationId
                    ? token.controlItemBgActive
                    : undefined,
              }}>
              <Typography.Link>{indication.title}</Typography.Link>
              {classification ? (
                <Tooltip
                  title={
                    COMPONENT_TYPE_INFO[classification.componentType].title
                  }>
                  <Tag
                    color={
                      classification.componentType === 'C' ? 'red' : 'green'
                    }>
                    Type {classification.componentType}
                  </Tag>
                </Tooltip>
              ) : (
                <Tag>Not classified</Tag>
              )}
            </List.Item>
          )
        }}
      />

      {selected ? (
        <LtaClassificationForm
          key={selected.indication.id}
          indication={selected.indication}
          savedClassification={selected.classification}
          componentId={componentId}
          inspectionId={inspectionId}
          componentModel={componentModel}
          remoteness={
            remotenessByIndication.get(selected.indication.id) ?? null
          }
          decidedBy={decidedBy}
          readonly={readonly}
          isSaving={isSaving}
          saveClassification={saveClassification}
        />
      ) : (
        <Typography.Text type='secondary'>
          Select an LTA above to classify it.
        </Typography.Text>
      )}
    </Container>
  )
}
